import React, { useState, useCallback, useEffect } from 'react';
import './HandMatrix.css';

const HandMatrix = () => {
  const ranks = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
  
  // Initialize selected hands state
  const [selectedHands, setSelectedHands] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragAction, setDragAction] = useState('add'); // 'add' or 'remove'
  const [percentageSlider, setPercentageSlider] = useState(0);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [savedRanges, setSavedRanges] = useState({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [rangeName, setRangeName] = useState('');
  
  // Apply theme to body element
  useEffect(() => {
    if (isDarkTheme) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('dark-theme', 'light-theme');
    };
  }, [isDarkTheme]);
  
  // Load saved ranges from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('pokerRanges');
    if (saved) {
      try {
        setSavedRanges(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved ranges:', error);
      }
    }
  }, []);
  
  // Generate all possible hands for verification
  const generateAllHands = () => {
    const allHands = new Set();
    
    // Generate pocket pairs
    ranks.forEach(rank => {
      allHands.add(`${rank}${rank}`);
    });
    
    // Generate suited and offsuit hands
    for (let i = 0; i < ranks.length; i++) {
      for (let j = i + 1; j < ranks.length; j++) {
        allHands.add(`${ranks[i]}${ranks[j]}s`); // suited
        allHands.add(`${ranks[i]}${ranks[j]}o`); // offsuit
      }
    }
    
    return Array.from(allHands);
  };
  
  // Hand rankings for percentage selection (best to worst) - Complete 169 hands
  const handRankings = [
    'AA', 'KK', 'QQ', 'JJ', 'AKs', 'TT', 'AQs', 'AJs', 'KQs', 'AKo',
    '99', 'ATs', 'AQo', 'KJs', 'KTs', 'QJs', 'KQo', 'A9s', 'AJo', 'QTs',
    '88', 'KJo', 'A8s', 'Q9s', 'J9s', 'ATo', 'A7s', 'KTo', 'A5s', 'A6s',
    'QJo', '77', 'A4s', 'A3s', 'A2s', 'J8s', 'K9s', 'T9s', 'QTo', 'Q8s',
    '66', 'A9o', 'J7s', 'T8s', 'K8s', 'JTo', '55', 'A8o', 'Q7s', '98s',
    'T7s', 'A7o', 'J6s', '87s', 'Q9o', 'T6s', 'A6o', '44', 'K7s', '97s',
    'A5o', 'J9o', 'Q6s', '76s', 'T5s', 'A4o', 'K6s', 'J5s', 'Q5s', '86s',
    'A3o', '33', '96s', 'T9o', 'Q8o', 'A2o', '75s', 'J8o', '22', '85s',
    'T4s', 'K5s', 'J4s', '74s', 'K9o', 'T8o', 'Q4s', '95s', 'J7o', '65s',
    'T3s', 'K4s', '94s', 'Q3s', 'J3s', '84s', 'Q7o', '54s', '64s', 'T7o',
    'K8o', 'J6o', '98o', 'T2s', 'Q2s', '87o', '73s', 'J2s', '97o', 'T6o',
    'K7o', '63s', '83s', '76o', 'K3s', '53s', '86o', 'K2s', '62s', 'T5o',
    '75o', 'J5o', '96o', 'Q6o', '52s', '74o', '43s', 'Q5o', 'K6o', '85o',
    'T4o', '72s', '65o', 'J4o', 'Q4o', '94o', '42s', 'K5o', 'T3o', '32s',
    'Q3o', '54o', 'J3o', '64o', '84o', 'T2o', '93o', '73o', 'Q2o', 'J2o',
    '63o', '92o', '83o', '53o', 'K4o', '82o', '62o', '52o', 'K3o', '43o',
    '72o', 'K2o', '42o', '32o'
  ];
  
  // Verify handRankings completeness (only in development)
  if (process.env.NODE_ENV === 'development') {
    const allPossibleHands = generateAllHands();
    const rankingsSet = new Set(handRankings);
    const allHandsSet = new Set(allPossibleHands);
    
    if (handRankings.length !== 169) {
      console.warn(`HandRankings has ${handRankings.length} hands, expected 169`);
    }
    
    if (rankingsSet.size !== handRankings.length) {
      console.warn('Duplicate hands found in handRankings');
    }
    
    const missingHands = allPossibleHands.filter(hand => !rankingsSet.has(hand));
    if (missingHands.length > 0) {
      console.warn('Missing hands in rankings:', missingHands);
    }
    
    const extraHands = handRankings.filter(hand => !allHandsSet.has(hand));
    if (extraHands.length > 0) {
      console.warn('Invalid hands in rankings:', extraHands);
    }
  }
  
  // Generate hand combinations
  const generateHandCombinations = () => {
    const combinations = [];
    
    for (let i = 0; i < ranks.length; i++) {
      const row = [];
      for (let j = 0; j < ranks.length; j++) {
        if (i === j) {
          // Pocket pairs (diagonal)
          row.push(`${ranks[i]}${ranks[j]}`);
        } else if (i < j) {
          // Suited hands (upper triangle)
          row.push(`${ranks[i]}${ranks[j]}s`);
        } else {
          // Offsuit hands (lower triangle)
          row.push(`${ranks[j]}${ranks[i]}o`);
        }
      }
      combinations.push(row);
    }
    
    return combinations;
  };
  
  const handCombinations = generateHandCombinations();
  
  // Convert selected hands to poker notation
  const convertToNotation = (hands) => {
    if (hands.size === 0) return 'None';
    
    const handArray = Array.from(hands).sort();
    const notation = [];
    const processedHands = new Set();
    
    // Check for pair ranges
    const pairs = handArray.filter(hand => hand.length === 2 && hand[0] === hand[1]);
    if (pairs.length > 0) {
      const pairRanks = pairs.map(pair => ranks.indexOf(pair[0])).sort((a, b) => a - b);
      
      // Check if it's all pairs (22+)
      if (pairs.length === 13) {
        notation.push('22+');
        pairs.forEach(pair => processedHands.add(pair));
      } else {
        // Find consecutive ranges
        let i = 0;
        while (i < pairRanks.length) {
          let start = pairRanks[i];
          let end = start;
          
          // Find consecutive pairs
          while (i + 1 < pairRanks.length && pairRanks[i + 1] === end + 1) {
            end = pairRanks[i + 1];
            i++;
          }
          
          const startPair = `${ranks[start]}${ranks[start]}`;
          const endPair = `${ranks[end]}${ranks[end]}`;
          
          if (start === end) {
            notation.push(startPair);
          } else if (start === 0) {
            notation.push(`${endPair}+`);
          } else {
            notation.push(`${endPair}-${startPair}`);
          }
          
          // Mark these pairs as processed
          for (let j = start; j <= end; j++) {
            processedHands.add(`${ranks[j]}${ranks[j]}`);
          }
          
          i++;
        }
      }
    }
    
    // Check for suited aces (A2s+)
    const suitedAces = handArray.filter(hand => hand.startsWith('A') && hand.endsWith('s') && hand.length === 3);
    if (suitedAces.length > 0) {
      const aceRanks = suitedAces.map(hand => ranks.indexOf(hand[1])).sort((a, b) => b - a);
      const lowestAce = aceRanks[aceRanks.length - 1];
      
      if (suitedAces.length === 12) {
        notation.push('A2s+');
        suitedAces.forEach(hand => processedHands.add(hand));
      } else if (aceRanks.length > 1 && aceRanks[0] - aceRanks[aceRanks.length - 1] === aceRanks.length - 1) {
        notation.push(`A${ranks[lowestAce]}s+`);
        suitedAces.forEach(hand => processedHands.add(hand));
      }
    }
    
    // Check for offsuit aces (A2o+)
    const offsuitAces = handArray.filter(hand => hand.startsWith('A') && hand.endsWith('o') && hand.length === 3);
    if (offsuitAces.length > 0) {
      const aceRanks = offsuitAces.map(hand => ranks.indexOf(hand[1])).sort((a, b) => b - a);
      const lowestAce = aceRanks[aceRanks.length - 1];
      
      if (offsuitAces.length === 12) {
        notation.push('A2o+');
        offsuitAces.forEach(hand => processedHands.add(hand));
      } else if (aceRanks.length > 1 && aceRanks[0] - aceRanks[aceRanks.length - 1] === aceRanks.length - 1) {
        notation.push(`A${ranks[lowestAce]}o+`);
        offsuitAces.forEach(hand => processedHands.add(hand));
      }
    }
    
    // Check for suited kings
    const suitedKings = handArray.filter(hand => hand.startsWith('K') && hand.endsWith('s') && hand.length === 3);
    if (suitedKings.length > 0) {
      const kingRanks = suitedKings.map(hand => ranks.indexOf(hand[1])).sort((a, b) => b - a);
      const lowestKing = kingRanks[kingRanks.length - 1];
      
      if (suitedKings.length === 11) {
        notation.push('K2s+');
        suitedKings.forEach(hand => processedHands.add(hand));
      } else if (kingRanks.length > 1 && kingRanks[0] - kingRanks[kingRanks.length - 1] === kingRanks.length - 1) {
        notation.push(`K${ranks[lowestKing]}s+`);
        suitedKings.forEach(hand => processedHands.add(hand));
      }
    }
    
    // Check for suited connectors
    const suitedConnectors = [];
    for (let i = 0; i < ranks.length - 1; i++) {
      const connector = `${ranks[i]}${ranks[i + 1]}s`;
      if (hands.has(connector)) {
        suitedConnectors.push(connector);
      }
    }
    
    if (suitedConnectors.length >= 3) {
      // Check if it's a consecutive range
      const connectorIndices = suitedConnectors.map(hand => ranks.indexOf(hand[0])).sort((a, b) => a - b);
      let isConsecutive = true;
      for (let i = 1; i < connectorIndices.length; i++) {
        if (connectorIndices[i] !== connectorIndices[i - 1] + 1) {
          isConsecutive = false;
          break;
        }
      }
      
      if (isConsecutive) {
        const lowest = suitedConnectors[suitedConnectors.length - 1];
        notation.push(`${lowest}+`);
        suitedConnectors.forEach(hand => processedHands.add(hand));
      }
    }
    
    // Add percentage if it matches our slider value
    if (percentageSlider > 0) {
      const totalHands = 169;
      const expectedHands = Math.ceil((percentageSlider / 100) * totalHands);
      if (Math.abs(hands.size - expectedHands) <= 2) { // Allow small variance
        notation.unshift(`${percentageSlider}%`);
      }
    }
    
    // Add remaining individual hands
    const remainingHands = handArray.filter(hand => !processedHands.has(hand));
    notation.push(...remainingHands);
    
    return notation.join(', ');
  };
  
  // Handle percentage slider change
  const handlePercentageChange = (event) => {
    const percentage = parseInt(event.target.value);
    setPercentageSlider(percentage);
    
    if (percentage === 0) {
      setSelectedHands(new Set());
      return;
    }
    
    if (percentage === 100) {
      // For 100%, select all hands from the matrix
      const allHands = new Set();
      handCombinations.forEach(row => {
        row.forEach(hand => allHands.add(hand));
      });
      setSelectedHands(allHands);
      return;
    }
    
    const totalHands = 169; // 13x13 matrix
    const handsToSelect = Math.ceil((percentage / 100) * totalHands);
    const topHands = handRankings.slice(0, handsToSelect);
    
    setSelectedHands(new Set(topHands));
  };
  
  // Helper functions to check if preset categories are fully selected
  const isPocketPairsSelected = () => {
    const pocketPairs = new Set();
    ranks.forEach(rank => {
      pocketPairs.add(`${rank}${rank}`);
    });
    return Array.from(pocketPairs).every(hand => selectedHands.has(hand));
  };
  
  const isSuitedAcesSelected = () => {
    const suitedAces = new Set();
    for (let i = 1; i < ranks.length; i++) {
      suitedAces.add(`A${ranks[i]}s`);
    }
    return Array.from(suitedAces).every(hand => selectedHands.has(hand));
  };
  
  const isSuitedKingsSelected = () => {
    const suitedKings = new Set();
    for (let i = 2; i < ranks.length; i++) {
      suitedKings.add(`K${ranks[i]}s`);
    }
    return Array.from(suitedKings).every(hand => selectedHands.has(hand));
  };
  
  const isSuitedConnectorsSelected = () => {
    const suitedConnectors = new Set();
    for (let i = 0; i < ranks.length - 1; i++) {
      suitedConnectors.add(`${ranks[i]}${ranks[i + 1]}s`);
    }
    return Array.from(suitedConnectors).every(hand => selectedHands.has(hand));
  };
  
  const isSuitedOneGappersSelected = () => {
    const suitedOneGappers = new Set();
    for (let i = 0; i < ranks.length - 2; i++) {
      suitedOneGappers.add(`${ranks[i]}${ranks[i + 2]}s`);
    }
    return Array.from(suitedOneGappers).every(hand => selectedHands.has(hand));
  };
  
  const isBroadwayHandsSelected = () => {
    const broadway = ['A', 'K', 'Q', 'J', 'T'];
    const broadwayHands = new Set();
    
    for (let i = 0; i < broadway.length; i++) {
      for (let j = 0; j < broadway.length; j++) {
        if (i === j) {
          broadwayHands.add(`${broadway[i]}${broadway[j]}`);
        } else if (i < j) {
          broadwayHands.add(`${broadway[i]}${broadway[j]}s`);
          broadwayHands.add(`${broadway[i]}${broadway[j]}o`);
        }
      }
    }
    return Array.from(broadwayHands).every(hand => selectedHands.has(hand));
  };
  
  const isOffSuitAcesSelected = () => {
    const offSuitAces = new Set();
    for (let i = 1; i < ranks.length; i++) {
      offSuitAces.add(`A${ranks[i]}o`);
    }
    return Array.from(offSuitAces).every(hand => selectedHands.has(hand));
  };
  
  const isSmallPairsSelected = () => {
    const smallPairs = new Set(['22', '33', '44', '55', '66']);
    return Array.from(smallPairs).every(hand => selectedHands.has(hand));
  };
  
  const isMediumPairsSelected = () => {
    const mediumPairs = new Set(['77', '88', '99', 'TT', 'JJ']);
    return Array.from(mediumPairs).every(hand => selectedHands.has(hand));
  };
  
  const isPremiumPairsSelected = () => {
    const premiumPairs = new Set(['QQ', 'KK', 'AA']);
    return Array.from(premiumPairs).every(hand => selectedHands.has(hand));
  };
  
  const isPremiumHandsSelected = () => {
    const premiumHands = new Set(['AA', 'KK', 'QQ', 'JJ', 'AKs', 'AKo', 'AQs', 'AJs', 'KQs']);
    return Array.from(premiumHands).every(hand => selectedHands.has(hand));
  };
  
  // Preset hand selection functions
  const selectPocketPairs = () => {
    const pocketPairs = new Set();
    ranks.forEach(rank => {
      pocketPairs.add(`${rank}${rank}`);
    });
    
    // Check if all pocket pairs are already selected
    const allSelected = Array.from(pocketPairs).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      // Remove all pocket pairs
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        pocketPairs.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      // Add all pocket pairs
      setSelectedHands(prev => new Set([...prev, ...pocketPairs]));
    }
    setPercentageSlider(0); // Reset slider when manual selection is made
  };
  
  const selectSuitedAces = () => {
    const suitedAces = new Set();
    for (let i = 1; i < ranks.length; i++) {
      suitedAces.add(`A${ranks[i]}s`);
    }
    
    const allSelected = Array.from(suitedAces).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        suitedAces.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...suitedAces]));
    }
    setPercentageSlider(0);
  };
  
  const selectSuitedKings = () => {
    const suitedKings = new Set();
    for (let i = 2; i < ranks.length; i++) {
      suitedKings.add(`K${ranks[i]}s`);
    }
    
    const allSelected = Array.from(suitedKings).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        suitedKings.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...suitedKings]));
    }
    setPercentageSlider(0);
  };
  
  const selectSuitedConnectors = () => {
    const suitedConnectors = new Set();
    for (let i = 0; i < ranks.length - 1; i++) {
      suitedConnectors.add(`${ranks[i]}${ranks[i + 1]}s`);
    }
    
    const allSelected = Array.from(suitedConnectors).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        suitedConnectors.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...suitedConnectors]));
    }
    setPercentageSlider(0);
  };
  
  const selectSuitedOneGappers = () => {
    const suitedOneGappers = new Set();
    for (let i = 0; i < ranks.length - 2; i++) {
      suitedOneGappers.add(`${ranks[i]}${ranks[i + 2]}s`);
    }
    
    const allSelected = Array.from(suitedOneGappers).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        suitedOneGappers.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...suitedOneGappers]));
    }
    setPercentageSlider(0);
  };
  
  const selectBroadwayHands = () => {
    const broadway = ['A', 'K', 'Q', 'J', 'T'];
    const broadwayHands = new Set();
    
    // All combinations of broadway cards
    for (let i = 0; i < broadway.length; i++) {
      for (let j = 0; j < broadway.length; j++) {
        if (i === j) {
          broadwayHands.add(`${broadway[i]}${broadway[j]}`);
        } else if (i < j) {
          broadwayHands.add(`${broadway[i]}${broadway[j]}s`);
          broadwayHands.add(`${broadway[i]}${broadway[j]}o`);
        }
      }
    }
    
    const allSelected = Array.from(broadwayHands).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        broadwayHands.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...broadwayHands]));
    }
    setPercentageSlider(0);
  };
  
  const selectOffSuitAces = () => {
    const offSuitAces = new Set();
    for (let i = 1; i < ranks.length; i++) {
      offSuitAces.add(`A${ranks[i]}o`);
    }
    
    const allSelected = Array.from(offSuitAces).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        offSuitAces.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...offSuitAces]));
    }
    setPercentageSlider(0);
  };
  
  const selectSmallPairs = () => {
    const smallPairs = new Set(['22', '33', '44', '55', '66']);
    
    const allSelected = Array.from(smallPairs).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        smallPairs.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...smallPairs]));
    }
    setPercentageSlider(0);
  };
  
  const selectMediumPairs = () => {
    const mediumPairs = new Set(['77', '88', '99', 'TT', 'JJ']);
    
    const allSelected = Array.from(mediumPairs).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        mediumPairs.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...mediumPairs]));
    }
    setPercentageSlider(0);
  };
  
  const selectPremiumPairs = () => {
    const premiumPairs = new Set(['QQ', 'KK', 'AA']);
    
    const allSelected = Array.from(premiumPairs).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        premiumPairs.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...premiumPairs]));
    }
    setPercentageSlider(0);
  };
  
  const selectPremiumHands = () => {
    const premiumHands = new Set(['AA', 'KK', 'QQ', 'JJ', 'AKs', 'AKo', 'AQs', 'AJs', 'KQs']);
    
    const allSelected = Array.from(premiumHands).every(hand => selectedHands.has(hand));
    
    if (allSelected) {
      setSelectedHands(prev => {
        const newSet = new Set(prev);
        premiumHands.forEach(hand => newSet.delete(hand));
        return newSet;
      });
    } else {
      setSelectedHands(prev => new Set([...prev, ...premiumHands]));
    }
    setPercentageSlider(0);
  };
  
  // Handle mouse down - start drag selection
  const handleMouseDown = useCallback((hand, event) => {
    event.preventDefault();
    setIsDragging(true);
    
    const newSelectedHands = new Set(selectedHands);
    
    if (newSelectedHands.has(hand)) {
      // If hand is already selected, we'll be in remove mode
      newSelectedHands.delete(hand);
      setDragAction('remove');
    } else {
      // If hand is not selected, we'll be in add mode
      newSelectedHands.add(hand);
      setDragAction('add');
    }
    
    setSelectedHands(newSelectedHands);
    setPercentageSlider(0); // Reset slider when manual selection is made
  }, [selectedHands]);
  
  // Handle mouse up - stop drag selection
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragAction('add');
    }
  }, [isDragging]);
  
  // Handle mouse enter during drag
  const handleMouseEnter = useCallback((hand) => {
    if (isDragging) {
      const newSelectedHands = new Set(selectedHands);
      
      if (dragAction === 'add') {
        newSelectedHands.add(hand);
      } else {
        newSelectedHands.delete(hand);
      }
      
      setSelectedHands(newSelectedHands);
    }
  }, [isDragging, dragAction, selectedHands]);
  
  // Add global mouse up listener
  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setDragAction('add');
      }
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);
  
  // Prevent context menu during drag
  React.useEffect(() => {
    const handleContextMenu = (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isDragging]);
  
  // Get cell class based on hand type and selection
  const getCellClass = (hand, row, col) => {
    let baseClass = 'hand-cell';
    
    // Add type-specific classes
    if (row === col) {
      baseClass += ' pocket-pair';
    } else if (row < col) {
      baseClass += ' suited';
    } else {
      baseClass += ' offsuit';
    }
    
    // Add selection class
    if (selectedHands.has(hand)) {
      baseClass += ' selected';
    }
    
    // Add drag mode class
    if (isDragging) {
      baseClass += ' dragging';
      baseClass += dragAction === 'add' ? ' adding' : ' removing';
    }
    
    return baseClass;
  };
  
  // Clear all selections
  const clearSelection = () => {
    setSelectedHands(new Set());
    setIsDragging(false);
    setDragAction('add');
    setPercentageSlider(0);
  };
  
  // Select all hands
  const selectAll = () => {
    const allHands = new Set();
    handCombinations.forEach(row => {
      row.forEach(hand => allHands.add(hand));
    });
    setSelectedHands(allHands);
    setIsDragging(false);
    setDragAction('add');
    setPercentageSlider(100);
  };

  // Calculate number of combos for selected hands
  const calculateCombos = (hands) => {
    let totalCombos = 0;
    
    hands.forEach(hand => {
      if (hand.length === 2 && hand[0] === hand[1]) {
        // Pocket pairs: 6 combinations each (e.g., AA has 6 combos: AsAh, AsAd, AsAc, AhAd, AhAc, AdAc)
        totalCombos += 6;
      } else if (hand.endsWith('s')) {
        // Suited hands: 4 combinations each (e.g., AKs has 4 combos: AsKs, AhKh, AdKd, AcKc)
        totalCombos += 4;
      } else if (hand.endsWith('o')) {
        // Offsuit hands: 12 combinations each (e.g., AKo has 12 combos: all non-suited combinations)
        totalCombos += 12;
      }
    });
    
    return totalCombos;
  };

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  // Save current range
  const saveRange = () => {
    if (!rangeName.trim()) {
      alert('Please enter a name for this range');
      return;
    }
    
    if (selectedHands.size === 0) {
      alert('Cannot save an empty range');
      return;
    }

    const rangeData = {
      hands: Array.from(selectedHands),
      timestamp: new Date().toISOString(),
      notation: convertToNotation(selectedHands),
      handCount: selectedHands.size,
      comboCount: calculateCombos(selectedHands)
    };

    const updatedRanges = {
      ...savedRanges,
      [rangeName.trim()]: rangeData
    };

    setSavedRanges(updatedRanges);
    localStorage.setItem('pokerRanges', JSON.stringify(updatedRanges));
    
    setShowSaveDialog(false);
    setRangeName('');
    alert(`Range "${rangeName.trim()}" saved successfully!`);
  };

  // Load a saved range
  const loadRange = (name) => {
    const rangeData = savedRanges[name];
    if (rangeData) {
      setSelectedHands(new Set(rangeData.hands));
      setPercentageSlider(0); // Reset percentage slider when loading
      setShowLoadDialog(false);
    }
  };

  // Delete a saved range
  const deleteRange = (name) => {
    if (window.confirm(`Are you sure you want to delete the range "${name}"?`)) {
      const updatedRanges = { ...savedRanges };
      delete updatedRanges[name];
      setSavedRanges(updatedRanges);
      localStorage.setItem('pokerRanges', JSON.stringify(updatedRanges));
    }
  };

  // Open save dialog
  const openSaveDialog = () => {
    if (selectedHands.size === 0) {
      alert('Please select some hands before saving');
      return;
    }
    setShowSaveDialog(true);
    setRangeName('');
  };

  // Open load dialog
  const openLoadDialog = () => {
    if (Object.keys(savedRanges).length === 0) {
      alert('No saved ranges found');
      return;
    }
    setShowLoadDialog(true);
  };

  return (
    <div className={`hand-matrix-container ${isDarkTheme ? 'dark-theme' : ''}`}>
      <button 
        className="theme-toggle" 
        onClick={toggleTheme}
        title={`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`}
      >
        {isDarkTheme ? 'Light Mode' : 'Dark Mode'}
      </button>
      
      <div className="matrix-header">
        <h2>Poker Hand Range Matrix</h2>
      </div>
      
      <div className="main-layout">
        <div className="left-panel">
          <div className="controls-section">
            <h3>Controls</h3>
            <div className="controls">
              <button onClick={clearSelection} className="control-btn clear-btn">
                Clear All
              </button>
              <button onClick={selectAll} className="control-btn select-all-btn">
                Select All
              </button>
              <button onClick={openSaveDialog} className="control-btn save-btn">
                Save Range
              </button>
              <button onClick={openLoadDialog} className="control-btn load-btn">
                Load Range
              </button>
              <div className="selected-count">
                Selected: {selectedHands.size} hands
              </div>
            </div>
          </div>

          <div className="percentage-selector">
            <h3>Select Top % of Hands</h3>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="100"
                value={percentageSlider}
                onChange={handlePercentageChange}
                className="percentage-slider"
              />
              <div className="slider-labels">
                <span>0%</span>
                <span className="current-percentage">{percentageSlider}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          <div className="preset-selections">
            <h3>Quick Select:</h3>
            <div className="preset-buttons">
              <button 
                onClick={selectPremiumHands} 
                className={`preset-btn premium ${isPremiumHandsSelected() ? 'active' : ''}`}
              >
                Premium Hands
              </button>
              <button 
                onClick={selectPocketPairs} 
                className={`preset-btn pairs ${isPocketPairsSelected() ? 'active' : ''}`}
              >
                All Pocket Pairs
              </button>
              <button 
                onClick={selectPremiumPairs} 
                className={`preset-btn premium-pairs ${isPremiumPairsSelected() ? 'active' : ''}`}
              >
                Premium Pairs (QQ+)
              </button>
              <button 
                onClick={selectMediumPairs} 
                className={`preset-btn medium-pairs ${isMediumPairsSelected() ? 'active' : ''}`}
              >
                Medium Pairs (77-JJ)
              </button>
              <button 
                onClick={selectSmallPairs} 
                className={`preset-btn small-pairs ${isSmallPairsSelected() ? 'active' : ''}`}
              >
                Small Pairs (22-66)
              </button>
              <button 
                onClick={selectSuitedAces} 
                className={`preset-btn suited-aces ${isSuitedAcesSelected() ? 'active' : ''}`}
              >
                Suited Aces
              </button>
              <button 
                onClick={selectOffSuitAces} 
                className={`preset-btn offsuit-aces ${isOffSuitAcesSelected() ? 'active' : ''}`}
              >
                Offsuit Aces
              </button>
              <button 
                onClick={selectSuitedKings} 
                className={`preset-btn suited-kings ${isSuitedKingsSelected() ? 'active' : ''}`}
              >
                Suited Kings
              </button>
              <button 
                onClick={selectSuitedConnectors} 
                className={`preset-btn suited-connectors ${isSuitedConnectorsSelected() ? 'active' : ''}`}
              >
                Suited Connectors
              </button>
              <button 
                onClick={selectSuitedOneGappers} 
                className={`preset-btn suited-gappers ${isSuitedOneGappersSelected() ? 'active' : ''}`}
              >
                Suited 1-Gappers
              </button>
              <button 
                onClick={selectBroadwayHands} 
                className={`preset-btn broadway ${isBroadwayHandsSelected() ? 'active' : ''}`}
              >
                Broadway Hands
              </button>
            </div>
          </div>
        </div>

        <div className="center-panel">
          <div className="legend">
            <div className="legend-item">
              <div className="legend-color pocket-pair"></div>
              <span>Pocket Pairs</span>
            </div>
            <div className="legend-item">
              <div className="legend-color suited"></div>
              <span>Suited</span>
            </div>
            <div className="legend-item">
              <div className="legend-color offsuit"></div>
              <span>Offsuit</span>
            </div>
          </div>
          
          <div 
            className="hand-matrix"
            onMouseUp={handleMouseUp}
            style={{ userSelect: 'none' }}
          >
            {handCombinations.map((row, rowIndex) => (
              <div key={rowIndex} className="matrix-row">
                {row.map((hand, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={getCellClass(hand, rowIndex, colIndex)}
                    onMouseDown={(e) => handleMouseDown(hand, e)}
                    onMouseEnter={() => handleMouseEnter(hand)}
                    title={
                      isDragging
                        ? `${hand} - ${dragAction === 'add' ? 'Adding' : 'Removing'} mode active`
                        : `${hand} - Click and drag to select multiple hands`
                    }
                  >
                    {hand}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="right-panel">
          <div className="usage-instructions">
            <h4>How to use:</h4>
            <ul>
              <li><strong>Single Selection:</strong> Click a hand to toggle it on/off</li>
              <li><strong>Drag Selection:</strong> Click and hold, then drag over hands to select/deselect multiple hands</li>
              <li><strong>Quick Select:</strong> Use preset buttons to select common hand categories</li>
              <li><strong>Bulk Actions:</strong> Use "Select All" or "Clear All" buttons</li>
            </ul>
          </div>
          
          <div className="selected-hands">
            <h3>Selected Range:</h3>
            
            <div className="range-stats">
              <div className="stat-item">
                <span className="stat-label">Hands:</span>
                <span className="stat-value">{selectedHands.size}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Combos:</span>
                <span className="stat-value">{calculateCombos(selectedHands)}</span>
              </div>
            </div>
            
            <div className="notation-section">
              <h4>Range Notation:</h4>
              <div className="notation-display">
                {convertToNotation(selectedHands)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Range Dialog */}
      {showSaveDialog && (
        <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Save Range</h3>
            <div className="modal-body">
              <div className="range-preview">
                <div className="preview-stats">
                  <span>Hands: {selectedHands.size}</span>
                  <span>Combos: {calculateCombos(selectedHands)}</span>
                </div>
                <div className="preview-notation">
                  {convertToNotation(selectedHands)}
                </div>
              </div>
              <input
                type="text"
                placeholder="Enter range name..."
                value={rangeName}
                onChange={(e) => setRangeName(e.target.value)}
                className="range-name-input"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && saveRange()}
              />
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowSaveDialog(false)} className="modal-btn cancel-btn">
                Cancel
              </button>
              <button onClick={saveRange} className="modal-btn save-btn">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Range Dialog */}
      {showLoadDialog && (
        <div className="modal-overlay" onClick={() => setShowLoadDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Load Range</h3>
            <div className="modal-body">
              <div className="saved-ranges-list">
                {Object.keys(savedRanges).length === 0 ? (
                  <div className="no-ranges">No saved ranges found</div>
                ) : (
                  Object.entries(savedRanges).map(([name, rangeData]) => (
                    <div key={name} className="saved-range-item">
                      <div className="range-info">
                        <div className="range-name">{name}</div>
                        <div className="range-details">
                          {rangeData.handCount} hands • {rangeData.comboCount} combos
                        </div>
                        <div className="range-notation">{rangeData.notation}</div>
                        <div className="range-timestamp">
                          {new Date(rangeData.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="range-actions">
                        <button 
                          onClick={() => loadRange(name)} 
                          className="action-btn load-action-btn"
                        >
                          Load
                        </button>
                        <button 
                          onClick={() => deleteRange(name)} 
                          className="action-btn delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowLoadDialog(false)} className="modal-btn cancel-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandMatrix; 